import { ActionButton, IconButton, MessageBarButton } from 'office-ui-fabric-react/lib/Button';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { Rating, RatingSize } from 'office-ui-fabric-react/lib/Rating';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import * as React from 'react';
// tslint:disable-next-line:no-duplicate-imports
import { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Review } from '../../store';
import { DirtyContext } from '../dirtyContext';
import PhotoUploader from '../photoUploader';
import { Thumbnail, ThumbnailProps } from '../thumbnailImage';
import classNames from './review.classNames';

export interface ReviewAttributes {
    token: string;
    review: Review;
    error: string;
    readOnly: boolean;
    files: File[];
}

export interface ReviewActions {
    fetch(token: string);
    submit(files: File[], review: Review, token: string, done: () => void);
    rate(rank: number);
    comment(text: string);
    author(text: string);
}

export type ReviewProps = ReviewAttributes & ReviewActions;

enum ReviewPageState {
    loading,
    loaded,
    disabled,
    dirty,
    updating,
    error
}

type ReviewState = {
    pageState: ReviewPageState;
    editingAuthor: boolean;
    statusMessage: string;
};

type SetState = React.Dispatch<React.SetStateAction<ReviewState>>;

const renderAuthor = (review: Review, state: ReviewState, setState: SetState, onChanged: (x: any) => void) => {
    const setAuthorState = (editingAuthor: boolean) => {
        setState({...state, editingAuthor});
    };
    const onKeyPress = (evt: React.KeyboardEvent<HTMLInputElement>) => {
        if (evt.key === 'Enter' || evt.key === 'Escape') {
            setAuthorState(false);
        }
    };

    const turnOnEditingAuthor = () => {
        setAuthorState(true);
    };

    return state.editingAuthor
    ? (
        <div className={classNames().author}>
            <TextField
                label='by'
                autoFocus
                borderless
                value={review.author}
                maxLength={48}
                onKeyPress={onKeyPress}
                onChanged={onChanged}
            />
        </div>)
    : (
        <div className={classNames().author}>
            by {review.author}
            <IconButton
                iconProps={{iconName: 'Edit'}}
                onClick={turnOnEditingAuthor}
            />
        </div>);
};

const renderMessageBar = (state: ReviewState, review: Review, onDismiss: () => void, history: any) => {
    const {statusMessage} = state;
    const {rewardPointsAdded} = review;
    if (!statusMessage) {
        return null;
    }
    const rewardMessage = rewardPointsAdded > 0 ? ` We have added ${rewardPointsAdded * 100} reward points to your account.` : null;
    // tslint:disable
    const showRewards = rewardPointsAdded ? <MessageBarButton text='Show rewards' onClick={() => history.push('/customer/rewardpoints')} /> : null;
    return (
        <MessageBar
            messageBarType={MessageBarType.success}
            isMultiline={false}
            onDismiss={onDismiss}
            actions={
                <div>
                    <MessageBarButton text='Show reviews' onClick={() => history.push('/reviews')} />
                    {showRewards}
                </div>
            }
        >
            {statusMessage}
            {rewardMessage}
        </MessageBar>
    );
    // tslint:enable
};

const orderPictures = (pictureUrls: string[]) => {
    if (!pictureUrls || pictureUrls.length === 0)  {
        return null;
    }

    const renderPicture = (pictureUrl: string, index: number) => <img className={classNames().orderPicture} src={pictureUrl} key={`order_picture_${index}`} alt={`order_picture_${index}`}/>;

    return (
        <div className={classNames().orderSummary}>
            <h3>Order summary</h3>
            <div className={classNames().orderPictures}>
            {pictureUrls.map(renderPicture)}
            </div>
        </div>
    );
};

export const reviewThumbnails = (review: Review): ThumbnailProps[] => review.pictureUrls.map((src, i) => ({
    src,
    thumbnail: review.thumbnailUrls.length > i ? review.thumbnailUrls[i] : undefined,
    maxHeight: 400,
    maxWidth: 400,
    contain: true
}));

const _ReviewEditor = (props: ReviewProps & RouteComponentProps) => {
    const [state, setState] = useState<ReviewState>({
        pageState: ReviewPageState.loading,
        editingAuthor: false,
        statusMessage: null
    });
    const setPageState = (pageState: ReviewPageState) => {
        setState({...state, pageState});
    };
    const { review, token, fetch, error, files, readOnly : disabled, submit, rate, comment, author } = props;
    const posting = state.pageState === ReviewPageState.updating;
    const clearStatusMessage = () => {
        setState({...state, statusMessage: null});
    };

    const showPictures = () => (
        <div className={classNames().photos}>
            {reviewThumbnails(review).map((thumbnailProps, i) => <Thumbnail {...thumbnailProps} key={`thumb_${i}`}/>)}
        </div>);
    const uploadPicture = () => disabled ? null : <PhotoUploader/>;
    const onSubmit = () => {
        setPageState(ReviewPageState.updating);
        submit(files, review, token, () => {
            setState({ ...state, statusMessage: 'Your review has been posted. Thank you.', pageState: ReviewPageState.loaded });
        });
    };

    const onChange = action => x => {
        setPageState(ReviewPageState.dirty);
        action(x);
    };
    const trackDirty = React.useCallback(
        (dirty: boolean) => {
            if (dirty) {
                setPageState(ReviewPageState.dirty);
            }
        },
        [setPageState]);

    const onChangeAuthor = onChange(author);
    if (!review) {
        fetch(token);

        return <Label>{error || 'Loading...'}</Label>;
    }

    return (
        <DirtyContext.Provider value={{ setDirty: trackDirty }}>
            <div className={classNames().review}>
                {renderMessageBar(state, review, clearStatusMessage, props.history)}
                <div className={classNames().top}>
                <Label className={classNames().title}>Review</Label>
                <ActionButton
                    className={posting ? classNames().posting : null}
                    text='Post'
                    disabled={state.pageState !== ReviewPageState.dirty}
                    onClick={onSubmit}
                    iconProps={{iconName: 'share'}}
                >
                    {posting ? <Spinner size={SpinnerSize.small}/> : null}
                </ActionButton>
                </div>
                <Label>Rate your satisfaction</Label>
                <Rating
                    min={0}
                    max={5}
                    disabled={disabled}
                    rating={review.rating}
                    size={RatingSize.Large}
                    onChanged={onChange(rate)}
                    styles={{ratingStarFront: { color: 'gold'}}}
                />
                {renderAuthor(review, state, setState, onChangeAuthor)}
                <TextField
                    label='Comment'
                    disabled={disabled}
                    className={classNames().comment}
                    multiline={true}
                    autoAdjustHeight={true}
                    maxLength={800}
                    placeholder='We would love to hear your feedback'
                    value={review.comment}
                    onChanged={onChange(comment)}
                />
                {showPictures()}
                {uploadPicture()}
                {orderPictures(review.orderPictureUrls)}
            </div>
        </DirtyContext.Provider>
        );
};

export const ReviewEditor = withRouter(_ReviewEditor);
